/** @jsxImportSource @emotion/react */
import { useTheme } from '@emotion/react';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import TextField from '@TextField';
import { useEffect, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import ValidationStatus from '../../Data/ValidationStatus';
import SagaStatus from '../../Data/SagaStatus';
import { getErrorMessage } from '../../Helpers/validation';
import {
  clearConstraintsValidations,
  setAutoCompleteInputValue,
  setAddressDetails,
  updateValue,
  setComponentValidationStatus,
} from '../../Lib/redux/slices/components';
import { setShouldNavigateForward } from '../../Lib/redux/slices/navigationControls';
import { getVisiblePageComponents } from '../Page/page.helpers';
import QuestionText from '../QuestionText/QuestionText';
import {
  getComponentValidationStatus,
  isEveryComponentValid,
} from '../__helpers__/component.helpers';
import {
  createAutoCompletePopper,
  isThisLastAddressComponentOnPage,
  renderMainTextWithHighlighting,
  showAllOptionsWhenNoMatches,
} from './googleAddress.helpers';
import makeLocationIconStyles from './makeLocationIconStyles';
import {
  getPlacePredictionsSaga,
  handleAnswerQuestionSideEffectsSaga,
  handleGoogleAddressSelectedSaga,
} from '../../Hooks/actionCreators';
import { useAutoFocusFirstEditableComponent } from '../../Hooks/useAutoFocusFirstEditableComponent';
import { renderMustache } from '../../Helpers/mustache';
import { getAutoCompleteName } from '../TextFieldInput/textfield.helpers';
import useGetExperimentName from '../../Hooks/useGetExperimentName/useGetExperimentName';
import { setComponentIdToReceiveFocus } from '../../Lib/redux/slices/persistKeyboard';

/**
 * GoogleAddressInput
 *
 * This component is used to select an address from a dropdown.
 * Options are fetched from Google Places API via the gateway.
 *
 */
const GoogleAddressInput = ({ componentId, iFrameId, text, formId }) => {
  const dispatch = useDispatch();
  const { countryCode, phoneNumberCountryCode, subcategoryId } = useSelector(
    (store) => store.formConfig
  );
  const { visitorId } = useSelector((store) => store.userIdentities);
  const { pages } = useSelector((store) => store.paging);
  const component = useSelector(
    (store) => store.components[`component_${componentId}`]
  );
  const { componentIdToReceiveFocus } = useSelector(
    (store) => store.persistKeyboard
  );
  const { componentIdToSagaStatus } = useSelector(
    (store) => store.dataMappings
  );
  const { displayQuestionTextInsideTextfield } = component.component;
  const mappedQuestionId = component?.component?.mappedQuestionId;
  const [userHasSelectedOption, setUserHasSelectedOption] = useState(false);
  const { currentPage } = useSelector((store) => store.navigation);
  const components = useSelector((store) => store.components);
  const { dynamicNewsletterName } = useSelector((store) => store.newsletter);
  const [isAutoFilled, setIsAutoFilled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [attemptNavigate, setAttemptNavigate] = useState(false);

  const errorMessage = getErrorMessage(component.validation?.constraints);
  const {
    options,
    value,
    inputValue,
    placeholder,
    restrictFromCountry,
    useLocationBias,
    shouldPopulateUpcomingFields,
  } = component.input;
  const theme = useTheme();
  const locationIconStyles = makeLocationIconStyles({ theme });
  const componentStatus = getComponentValidationStatus({ component });
  const isAnswered =
    componentStatus === ValidationStatus.VALID ||
    componentStatus === ValidationStatus.INVALID;

  const sagaStatus = componentIdToSagaStatus?.[componentId];

  const textfieldExperimentName = useGetExperimentName({
    componentId,
    componentName: 'TextField',
  });
  useEffect(() => {
    /**
     * Attempt Navigation: if all components on this page are valid and answered
     */
    const pageComponentIds = pages[currentPage];
    const visiblePageComponents = getVisiblePageComponents(
      pageComponentIds,
      components,
      dynamicNewsletterName,
      phoneNumberCountryCode,
      subcategoryId
    );

    const shouldPreventNavigation =
      sagaStatus === SagaStatus.PENDING && sagaStatus !== undefined;

    if (
      !shouldPreventNavigation &&
      isEveryComponentValid({
        components: visiblePageComponents,
        countryCode,
      }) &&
      componentStatus === ValidationStatus.VALID &&
      userHasSelectedOption &&
      isThisLastAddressComponentOnPage({ components, componentId })
    ) {
      setUserHasSelectedOption(false);
      dispatch(setShouldNavigateForward({ shouldNavigateForward: true }));
    }
  }, [
    currentPage,
    pages,
    components,
    countryCode,
    dispatch,
    componentStatus,
    componentId,
    userHasSelectedOption,
    dynamicNewsletterName,
    phoneNumberCountryCode,
    subcategoryId,
    sagaStatus,
  ]);

  useEffect(() => {
    const shouldPreventNavigation =
      sagaStatus === SagaStatus.PENDING && sagaStatus !== undefined;

    if (
      componentStatus === ValidationStatus.VALID &&
      attemptNavigate &&
      !shouldPreventNavigation
    ) {
      setAttemptNavigate(false);
      dispatch(setShouldNavigateForward({ shouldNavigateForward: true }));
    }
    if (
      componentStatus === ValidationStatus.INVALID &&
      attemptNavigate &&
      !shouldPreventNavigation
    ) {
      setAttemptNavigate(false);
      dispatch(setShouldNavigateForward({ shouldNavigateForward: false }));
    }
  }, [componentStatus, attemptNavigate, dispatch, sagaStatus]);

  useEffect(() => {
    if (isAutoFilled && value && value.trim()) {
      batch(() => {
        dispatch(
          setComponentValidationStatus({
            countryCode,
            componentId,
            iFrameId,
            formId,
          })
        );
        dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
      });
      setIsAutoFilled(false);
    }
  }, [
    value,
    isAutoFilled,
    dispatch,
    countryCode,
    componentId,
    iFrameId,
    formId,
  ]);

  const autoCompleteRef = useRef();
  useEffect(() => {
    const inputElement = autoCompleteRef.current?.querySelector('input');
    if (inputElement && componentIdToReceiveFocus === componentId) {
      inputElement.focus({ preventScroll: true });
      dispatch(
        setComponentIdToReceiveFocus({ componentIdToReceiveFocus: null })
      );
    }
  }, [componentIdToReceiveFocus, dispatch, componentId]);

  useAutoFocusFirstEditableComponent({
    componentId,
    inputRef: autoCompleteRef,
  });

  const handleInputChange = (event, newInputValue) => {
    if (event && event.type === 'click') {
      // i.e. if the user has selected an option from the dropdown not changed the input value
      const newInputValueInOptions = options.some(
        (option) => option.description === newInputValue
      );

      if (newInputValueInOptions) {
        setUserHasSelectedOption(true);
      }
    }
    if (isAutoFilled) {
      batch(() => {
        dispatch(
          setAutoCompleteInputValue({ componentId, value: newInputValue })
        );
        dispatch(updateValue({ componentId, value: newInputValue }));
        dispatch(setAddressDetails({ componentId, addressDetails: null }));
        dispatch(
          setComponentValidationStatus({
            countryCode,
            componentId,
            iFrameId,
            formId,
          })
        );
        dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
      });

      const inputElement = autoCompleteRef.current?.querySelector('input');
      if (inputElement) {
        inputElement.blur();
      }
    }

    if (!isAutoFilled && event && event.type === 'change') {
      setUserHasSelectedOption(false);

      // Clear address state on manual input (not from autocomplete)
      if (shouldPopulateUpcomingFields) {
        // Clear any populated address fields when user manually types
        dispatch(
          handleAnswerQuestionSideEffectsSaga({
            componentId,
          })
        );
      }

      dispatch(
        getPlacePredictionsSaga({
          componentId,
          input: newInputValue,
          visitorId,
          regionCode: restrictFromCountry
            ? countryCode.toLowerCase()
            : undefined,
          locationBias: useLocationBias ? 'headers' : undefined,
        })
      );

      batch(() => {
        dispatch(
          setAutoCompleteInputValue({ componentId, value: newInputValue })
        );
        dispatch(updateValue({ componentId, value: newInputValue }));
        // Clear addressDetails to ensure fallback validation is used
        dispatch(setAddressDetails({ componentId, addressDetails: null }));
      });
    }
  };

  const handleChange = async (event, addressValue) => {
    if (event.type !== 'click') {
      // if the user clicked they selected the input field, so we don't want to set as selected and thus prevent navigation. An input change is where the user has selected an option.
      setUserHasSelectedOption(false);
    }

    // Check if addressValue is a valid option object (has placeId)
    // If it's just a string (free text), handle it as manual input
    if (typeof addressValue === 'string' || !addressValue?.placeId) {
      // Handle free text input (e.g., when user presses Enter without selecting an option)
      const inputValueAsString =
        typeof addressValue === 'string'
          ? addressValue
          : addressValue?.description || '';

      batch(() => {
        dispatch(
          setAutoCompleteInputValue({ componentId, value: inputValueAsString })
        );
        dispatch(updateValue({ componentId, value: inputValueAsString }));
        dispatch(setAddressDetails({ componentId, addressDetails: null }));
        dispatch(
          setComponentValidationStatus({
            countryCode,
            componentId,
            iFrameId,
            formId,
          })
        );
        dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
      });
      return;
    }

    setUserHasSelectedOption(true);
    dispatch(
      handleGoogleAddressSelectedSaga({
        componentId,
        placeId: addressValue.placeId,
        inputValue: addressValue.description,
        formId,
        iFrameId,
        visitorId,
        shouldPopulateUpcomingFields,
      })
    );
  };

  const handleBlur = (event) => {
    event.preventDefault();
    if (inputValue === '' || inputValue === undefined || isAutoFilled) {
      return;
    }
    setUserHasSelectedOption(false);
    // Only update value, no validation on blur
    batch(() => {
      dispatch(setAutoCompleteInputValue({ componentId, value }));
      dispatch(updateValue({ componentId, value }));
    });
  };

  const handleKeyDown = (event) => {
    // Browser auto fill detection
    if (event.key === 'Unidentified' && event.code === undefined) {
      setIsAutoFilled(true);
      return;
    }

    if (event.key === 'Enter') {
      if (value && value.trim()) {
        event.preventDefault();
        batch(() => {
          dispatch(
            setComponentValidationStatus({
              countryCode,
              componentId,
              iFrameId,
              formId,
            })
          );
          dispatch(handleAnswerQuestionSideEffectsSaga({ componentId }));
        });
        setAttemptNavigate(true);
      }
    }
  };

  const renderAutocompleteOption = (props, option) => {
    return (
      <li {...props}>
        <Grid container alignItems="center">
          <Grid item>
            <LocationOnIcon css={locationIconStyles.icon} />
          </Grid>
          <Grid item xs>
            {renderMainTextWithHighlighting(option)}

            <Typography variant="body2" color="textSecondary">
              {option?.secondaryText?.text || ''}
            </Typography>
          </Grid>
        </Grid>
      </li>
    );
  };

  const renderInput = (params) => (
    <TextField
      inputProps={{
        ...params.inputProps,
        autoComplete: getAutoCompleteName(null, mappedQuestionId),
      }}
      inputRef={params.InputProps.ref}
      fullWidth
      type="text"
      placeholder={placeholder}
      label={displayQuestionTextInsideTextfield ? text : ''}
      dataTestId="googleAddressTextField"
      errorMessage={errorMessage}
      onClick={() => {
        setUserHasSelectedOption(false);
        dispatch(clearConstraintsValidations({ componentId }));
      }}
      isAnswered={isAnswered}
      iconType="locationService"
      shrinkLabel={displayQuestionTextInsideTextfield}
      experimentName={textfieldExperimentName}
    />
  );

  return (
    <>
      {!displayQuestionTextInsideTextfield && (
        <QuestionText
          heading={renderMustache({
            template: text,
            components,
            currentComponent: component,
            iFrameId,
            formId,
          })}
        />
      )}
      <Box
        marginTop={theme.mvf.spacing.medium}
        data-testid="googleAddressContainer"
      >
        <Autocomplete
          freeSolo
          disableClearable
          getOptionLabel={(option) =>
            typeof option === 'string' ? option : option.description
          }
          autoComplete
          id={`google-address-${componentId}`}
          inputValue={value || ''}
          onInputChange={handleInputChange}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          options={options || []}
          isOptionEqualToValue={(option, addressValue) =>
            option === addressValue
          }
          onBlur={handleBlur}
          PopperComponent={createAutoCompletePopper(autoCompleteRef)}
          ref={autoCompleteRef}
          renderInput={renderInput}
          renderOption={renderAutocompleteOption}
          filterOptions={showAllOptionsWhenNoMatches}
          open={isOpen}
          onOpen={() => !isAutoFilled && setIsOpen(true)}
          onClose={() => setIsOpen(false)}
        />
      </Box>
    </>
  );
};

export default GoogleAddressInput;
